import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Stack,
  SvgIcon,
  useTheme,
  useMediaQuery,
  Typography,
  Tooltip,
} from '@mui/material';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Publish as PublishIcon,
} from '@mui/icons-material';
import { Plus } from '../../../assets';
import { useNavigate } from 'react-router-dom';
import { complianceService } from '../../../app/services/api/compliance/complianceService';
import { UserContext } from '../../../components/shared/useUser';
import { AuditTemplate, AuditType, ComplianceFranchise } from '../../../pages/Compliance/Interfaces/complicanceInterfaces';
import PageArea from '../../../components/shared/PageArea';
import PageTitle from '../../../components/shared/PageTitle';
import { ConfirmationDeleteDialog } from '../../../components/shared/dialogs/ConfirmationDeleteDialog';
import { Section } from '../../../components/ui/Section';
import { Pill } from '../../../components/shared/Pill';
import { EmptyTable } from '../../../components/shared/EmptyTable';
import CustomPagination from '../../../components/forms/Pagination';

const AuditTemplatesPage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation('complianceAudits');
  const theme = useTheme();
  const isMobile = useMediaQuery(`(max-width:${theme.breakpoints.values.md}px)`);
  const { selectedStore, hasPermissionTo } = useContext(UserContext);
  const canCreate = hasPermissionTo(['AuditTemplatesCreate']);
  const canEdit = hasPermissionTo(['AuditTemplatesEdit']);
  const canDelete = hasPermissionTo(['AuditTemplatesDelete']);
  const canPublish = hasPermissionTo(['AuditTemplatesPublish']);
  const hasAnyAction = canEdit || canDelete || canPublish;
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [templates, setTemplates] = useState<AuditTemplate[]>([]);
  const [auditTypes, setAuditTypes] = useState<AuditType[]>([]);
  const [tenantFranchises, setTenantFranchises] = useState<ComplianceFranchise[]>([]);
  const [page, setPage] = useState(0);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingTemplate, setDeletingTemplate] = useState<AuditTemplate | null>(null);
  const [publishDialogOpen, setPublishDialogOpen] = useState(false);
  const [publishingTemplate, setPublishingTemplate] = useState<AuditTemplate | null>(null);
  const [newTemplate, setNewTemplate] = useState({
    auditTypeId: 0,
    name: '',
    description: '',
  });
  const [selectedFranchises, setSelectedFranchises] = useState<ComplianceFranchise[]>([]);
  const [allFranchisesSelected, setAllFranchisesSelected] = useState(false);

  const rowsPerPage = 10;

  const maxPages = () => {
    return Math.ceil(templates.length / rowsPerPage);
  };

  const paginatedTemplates = templates.slice(page * rowsPerPage, (page + 1) * rowsPerPage);

  useEffect(() => {
    setPage(0);
  }, [selectedStore?.storeNumber]);

  useEffect(() => {
    loadData();
  }, [page, selectedStore?.storeNumber]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      const [templatesData, typesData, franchisesData] = await Promise.all([
        complianceService.auditTemplates.getAll(undefined, true, selectedStore?.storeNumber),
        complianceService.auditTypes.getAll(false, selectedStore?.storeNumber),
        selectedStore?.storeNumber
          ? complianceService.tenantFranchises.getAll(selectedStore.storeNumber)
          : Promise.resolve([]),
      ]);
      setTemplates(templatesData);
      setAuditTypes(typesData);
      setTenantFranchises(franchisesData);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('common.failedToLoadData'));
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    try {
      setError(null);
      setSuccess(null);
      const selectedType = auditTypes.find(t => t.id === newTemplate.auditTypeId);
      if (!selectedType) return;

      const created = await complianceService.auditTemplates.create({
        ...newTemplate,
        auditTypeName: selectedType.name,
        version: 1,
        isPublished: false,
        createdBy: 0,
        createdByName: '',
        sections: [],
        totalQuestions: 0,
        totalPossibleScore: 0,
        franchises: selectedFranchises.map(f => ({
          franchiseId: f.franchiseId,
          franchiseName: f.franchiseName,
        })),
        passingScore: null,
        amberScore: null,
      }, selectedStore?.storeNumber);
      setCreateDialogOpen(false);
      setSelectedFranchises([]);
      setAllFranchisesSelected(false);
      setSuccess(t('templates.createSuccess'));
      setTimeout(() => setSuccess(null), 3000);
      navigate(`/compliance/audits/templates/builder/${created.id}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('templates.createFailed'));
    }
  };

  const handlePublishClick = (template: AuditTemplate, event: React.MouseEvent) => {
    event.stopPropagation();
    setPublishingTemplate(template);
    setPublishDialogOpen(true);
  };

  const handleConfirmPublish = async () => {
    if (!publishingTemplate) return;
    try {
      setError(null);
      setSuccess(null);
      await complianceService.auditTemplates.publish(publishingTemplate.id, selectedStore?.storeNumber);
      setSuccess(t('templates.publishSuccess'));
      setPublishDialogOpen(false);
      setPublishingTemplate(null);
      await loadData();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('templates.publishFailed'));
      setPublishDialogOpen(false);
      setPublishingTemplate(null);
    }
  };

  const handleDeleteClick = (template: AuditTemplate, event: React.MouseEvent) => {
    event.stopPropagation();
    setDeletingTemplate(template);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!deletingTemplate) return;
    try {
      setError(null);
      setSuccess(null);
      await complianceService.auditTemplates.delete(deletingTemplate.id, selectedStore?.storeNumber);
      setSuccess(t('templates.deleteSuccess'));
      setDeleteDialogOpen(false);
      setDeletingTemplate(null);
      await loadData();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('templates.deleteFailed'));
      setDeleteDialogOpen(false);
      setDeletingTemplate(null);
    }
  };

  const handleRowClick = (template: AuditTemplate) => {
    navigate(`/compliance/audits/templates/builder/${template.id}`);
  };

  if (loading) {
    return (
      <PageArea>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            py: 8,
          }}
        >
          <CircularProgress />
        </Box>
      </PageArea>
    );
  }

  return (
    <PageArea>
      <Stack
        width='100%'
        spacing={2}
        direction={isMobile ? 'column' : 'row'}
        sx={{
          justifyContent: 'space-between',
          mb: 3,
        }}
      >
        <PageTitle subTitle={t('templates.pageSubTitle')}>{t('templates.pageTitle')}</PageTitle>
        {canCreate && (
          <Button
            variant="primary"
            size="lg"
            startIcon={<SvgIcon><Plus /></SvgIcon>}
            onClick={() => setCreateDialogOpen(true)}
          >
            {t('templates.newTemplate')}
          </Button>
        )}
      </Stack>

      {error && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          onClose={() => setError(null)}
        >
          {error}
        </Alert>
      )}

      {success && (
        <Alert
          severity="success"
          sx={{ mb: 2 }}
          onClose={() => setSuccess(null)}
        >
          {success}
        </Alert>
      )}

      <Section>
        {templates.length === 0 ? (
          <EmptyTable
            bodyRowHeight={40}
            headerHeight={45}
            text={t('templates.noTemplates')}
            searchTerm={''}
            onClear={() => {}}
            onAdd={() => setCreateDialogOpen(true)}
            onAddIsCreating={false}
            addItemText={t('templates.newTemplate')}
            onAddIsDisabled={!canCreate}
            withPaginationShown={false}
          />
        ) : (
          <>
            <Box
              sx={{
                p: 5,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                width: '100%',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography
                  variant="textMD"
                  fontWeight={600}
                >
                  {t('templates.pageTitle')}
                </Typography>
                <Pill text={t('templates.templateCount', { count: templates.length })} />
              </Box>
            </Box>

            <TableContainer>
              <Table sx={{ borderBottomLeftRadius: '12px', borderBottomRightRadius: '12px', minWidth: '1000px' }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{t('common.name')}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{t('templates.auditType')}</TableCell>
                    <TableCell
                      align="center"
                      sx={{ whiteSpace: 'nowrap' }}
                    >
                      {t('common.version')}
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{ whiteSpace: 'nowrap' }}
                    >
                      {t('templates.questions')}
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{ whiteSpace: 'nowrap' }}
                    >
                      {t('common.status')}
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{ whiteSpace: 'nowrap', width: '200px' }}
                    >
                      {hasAnyAction && t('common.actions')}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedTemplates.map((template) => (
                    <TableRow
                      key={template.id}
                      onClick={() => canEdit && handleRowClick(template)}
                      sx={{
                        cursor: canEdit ? 'pointer' : 'default',
                        '&:hover': {
                          backgroundColor: canEdit ? 'action.hover' : undefined,
                        },
                      }}
                    >
                      <TableCell>
                        <Box>
                          <Tooltip title={template.name}>
                            <Box
                              sx={{
                                fontWeight: 500,
                                maxWidth: '300px',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {template.name}
                            </Box>
                          </Tooltip>
                          {template.description && (
                            <Tooltip title={template.description}>
                              <Box
                                sx={{
                                  fontSize: '0.875rem',
                                  color: theme.palette.custom.gray[600],
                                  maxWidth: '300px',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                {template.description}
                              </Box>
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Tooltip title={template.auditTypeName}>
                          <span
                            style={{
                              display: 'block',
                              maxWidth: '200px',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {template.auditTypeName}
                          </span>
                        </Tooltip>
                      </TableCell>
                      <TableCell align="center">v{template.version}</TableCell>
                      <TableCell align="center">
                        <Box>
                          <Box sx={{ fontWeight: 500 }}>{template.totalQuestions}</Box>
                          <Box
                            sx={{
                              fontSize: '0.75rem',
                              color: theme.palette.custom.gray[600],
                            }}
                          >
                            ({template.totalPossibleScore} {t('common.pts')})
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={template.isPublished ? t('common.published') : t('common.draft')}
                          color={template.isPublished ? 'success' : 'warning'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="right">
                        {canEdit && (
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/compliance/audits/templates/builder/${template.id}`);
                            }}
                            title={t('common.edit')}
                            sx={{ mr: 1 }}
                          >
                            <EditIcon />
                          </IconButton>
                        )}
                        {canPublish && !template.isPublished && (
                          <IconButton
                            size="small"
                            onClick={(e) => handlePublishClick(template, e)}
                            title={t('templates.publish')}
                            color="primary"
                            sx={{ mr: 1 }}
                          >
                            <PublishIcon />
                          </IconButton>
                        )}
                        {canDelete && (
                          <IconButton
                            size="small"
                            onClick={(e) => handleDeleteClick(template, e)}
                            color="error"
                            title={t('common.delete')}
                          >
                            <DeleteIcon />
                          </IconButton>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <CustomPagination
              page={page}
              setPage={setPage}
              maxPages={maxPages()}
              breakpointForChangeDisplay={120000}
              useNoDataMessage={false}
            />
          </>
        )}
      </Section>

      {/* Create Dialog */}
      <Dialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t('templates.newTemplateDialog')}</DialogTitle>
        <DialogContent>
          <Box
            display="flex"
            flexDirection="column"
            gap={2}
            pt={1}
          >
            <TextField
              select
              label={t('templates.auditType')}
              value={newTemplate.auditTypeId}
              onChange={(e) => setNewTemplate({ ...newTemplate, auditTypeId: Number(e.target.value) })}
              required
              fullWidth
            >
              {auditTypes.map((type) => (
                <MenuItem
                  key={type.id}
                  value={type.id}
                >
                  {type.name}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label={t('templates.templateName')}
              value={newTemplate.name}
              onChange={(e) => setNewTemplate({ ...newTemplate, name: e.target.value })}
              required
              fullWidth
            />
            <TextField
              label={t('common.description')}
              value={newTemplate.description}
              onChange={(e) => setNewTemplate({ ...newTemplate, description: e.target.value })}
              multiline
              rows={3}
              fullWidth
            />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Checkbox
                checked={allFranchisesSelected}
                onChange={(e) => {
                  const checked = e.target.checked;
                  setAllFranchisesSelected(checked);
                  setSelectedFranchises(checked ? [...tenantFranchises] : []);
                }}
              />
              <Typography variant="textSM">{t('templateBuilder.selectAllFranchises')}</Typography>
            </Box>
            <Autocomplete
              multiple
              options={tenantFranchises}
              disableCloseOnSelect
              getOptionLabel={(option) => option.franchiseName || ''}
              isOptionEqualToValue={(option, value) => option.franchiseId === value.franchiseId}
              value={selectedFranchises}
              onChange={(_event, newValue) => {
                setSelectedFranchises(newValue);
                setAllFranchisesSelected(tenantFranchises.length > 0 && newValue.length === tenantFranchises.length);
              }}
              renderOption={(props, option, { selected }) => (
                <li {...props}>
                  <Checkbox
                    icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                    checkedIcon={<CheckBoxIcon fontSize="small" />}
                    style={{ marginRight: 8 }}
                    checked={selected}
                  />
                  {option.franchiseName}
                </li>
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    {...getTagProps({ index })}
                    key={option.franchiseId}
                    label={option.franchiseName}
                    size="small"
                  />
                ))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t('templateBuilder.selectFranchises')}
                  placeholder={t('templateBuilder.selectFranchises')}
                  required
                />
              )}
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setCreateDialogOpen(false);
            setSelectedFranchises([]);
            setAllFranchisesSelected(false);
          }}>{t('common.cancel')}</Button>
          <Button
            onClick={handleCreate}
            variant="primary"
            disabled={!newTemplate.auditTypeId || !newTemplate.name.trim() || selectedFranchises.length === 0}
          >
            {t('templates.createAndBuild')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <ConfirmationDeleteDialog
        open={deleteDialogOpen}
        onConfirm={handleConfirmDelete}
        onCancel={() => {
          setDeleteDialogOpen(false);
          setDeletingTemplate(null);
        }}
        nameOfObject={deletingTemplate?.name || t('templates.template')}
      />

      {/* Publish Confirmation Dialog */}
      <Dialog
        open={publishDialogOpen}
        onClose={() => setPublishDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t('templates.publishTemplate')}</DialogTitle>
        <DialogContent>
          <Box pt={1}>
            <Typography
              variant="textSM"
              sx={{ mb: 2 }}
            >
              {t('templates.publishConfirm')}
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 2 }}>
              <Typography variant="textSM">
                <strong>{t('common.name')}:</strong> {publishingTemplate?.name}
              </Typography>
              <Typography
                variant="textSM"
                color="text.secondary"
              >
                <strong>{t('common.version')}:</strong> v{publishingTemplate?.version} — {publishingTemplate?.auditTypeName}
              </Typography>
              <Typography
                variant="textSM"
                color="text.secondary"
              >
                <strong>{t('templates.questions')}:</strong> {publishingTemplate?.totalQuestions} — {publishingTemplate?.totalPossibleScore} {t('common.pts')}
              </Typography>
            </Box>
            <Alert
              severity="info"
            >
              {t('templates.publishWarning')}
            </Alert>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setPublishDialogOpen(false)}
            size="lg"
          >
            {t('common.cancel')}
          </Button>
          <Button
            onClick={handleConfirmPublish}
            variant="primary"
            size="lg"
          >
            {t('templates.publish')}
          </Button>
        </DialogActions>
      </Dialog>
    </PageArea>
  );
};

export default AuditTemplatesPage;
